import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Badge, 
  Alert, 
  Spinner, 
  Nav, 
  Tab,
  Form,
  Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles,
  faMagic,
  faBookmark,
  faTimes,
  faTrophy,
  faHome,
  faBrain,
  faFilter,
  faTable,
  faList,
  faSearch,
  faCheckCircle,
  faMap,
  faEnvelope,
  faBuilding,
  faHistory,
  faShare
} from '@fortawesome/free-solid-svg-icons';
import PropertyCard from '../../common/PropertyCard';
import CompactPropertyCard from './CompactPropertyCard';
import PropertyDetailsInline from './PropertyDetailsInline';
import PropertyMapbox from './PropertyMapbox';
import axiosInstance from '../../../helpers/api';
import { toast } from 'react-hot-toast';
import './AISearchTab.css';

interface AISearchTabProps {
  leadId: string;
  clientName: string;
  buyerProfile?: any;
}

interface SavedSearch {
  id?: string;
  query: string;
  createdAt?: string;
  resultsCount?: number;
  emailFrequency?: string;
  isActive?: boolean;
  newListingsCount?: number;
  lastSearchRun?: string;
  searchCriteria?: any;
  mlsIds?: string[];
  newListings?: string[];
  shareUrl?: string;
  shareSlug?: string;
}

interface SearchResults {
  topMatches: any[];
  allProperties: any[];
  totalCount: number;
  searchQuery: string;
  filters?: any;
}

const AISearchTab: React.FC<AISearchTabProps> = ({ leadId, clientName, buyerProfile }) => {
  // State management
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResults | null>(null);
  const [resultsView, setResultsView] = useState<string>('top-matches');
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'cards' | 'table' | 'map'>('cards');
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedPropertiesToSend, setSelectedPropertiesToSend] = useState<Set<string>>(new Set());
  const [searchHistory, setSearchHistory] = useState<SavedSearch[]>([]);
  const [showSearchHistory, setShowSearchHistory] = useState(false);
  
  // Refinement state
  const [showRefinements, setShowRefinements] = useState(false);
  const [refinementText, setRefinementText] = useState('');
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  
  // Inline property details state
  const [expandedProperties, setExpandedProperties] = useState<Set<string>>(new Set());
  const [propertyNotes, setPropertyNotes] = useState<{ [key: string]: string }>({});

  // Load saved searches and history on component mount
  useEffect(() => {
    loadSavedSearches();
    loadSearchHistory();
  }, [leadId]);

  const loadSavedSearches = async () => {
    try {
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadId}/saved-searches`);
      if (response.data.success) {
        setSavedSearches(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load saved searches:', error);
    }
  };

  const loadSearchHistory = async () => {
    try {
      const response = await axiosInstance.get(`/lead-engagement/leads/${leadId}/search-history`);
      if (response.data.success) {
        setSearchHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to load search history:', error);
    }
  };

  const saveSearchToHistory = async (query: string, criteria: any, properties: any[]) => {
    try {
      const mlsIds = properties.map(p => p.mls_number || p.id).filter(Boolean);
      
      const response = await axiosInstance.post('/lead-engagement/search-history', {
        buyerId: leadId,
        searchQuery: query,
        searchCriteria: criteria,
        resultsCount: properties.length,
        mlsIds,
        aiSessionId: `lead-${leadId}-${Date.now()}`,
        agentNotes: '',
        emailFrequency: 'daily'
      });

      if (response.data.success) {
        // Reload search history to show the new search
        loadSearchHistory();
        
        if (response.data.data.newListingsCount > 0) {
          toast.success(`${response.data.data.newListingsCount} new listings since last search!`);
        }
        
        // Show share URL if available
        if (response.data.data.shareUrl) {
          console.log('Share URL generated:', response.data.data.shareUrl);
          // Could show a toast or modal with the share URL
        }
      }
    } catch (error) {
      console.error('Failed to save search to history:', error);
    }
  };

  // Parse natural language into search criteria
  const parseNaturalLanguage = (text: string) => {
    const criteria: any = {};
    const lowerText = text.toLowerCase();
    
    // Extract location (cities, areas)
    const locations = [
      'gilbert', 'chandler', 'mesa', 'scottsdale', 'phoenix', 'tempe', 
      'paradise valley', 'cave creek', 'fountain hills', 'queen creek',
      'east valley', 'west valley', 'north scottsdale', 'south tempe'
    ];
    
    for (const location of locations) {
      if (lowerText.includes(location)) {
        criteria.location = location;
        break;
      }
    }
    
    // Extract price
    const priceMatch = lowerText.match(/under\s*\$?(\d+)k?|max\s*\$?(\d+)k?|less than\s*\$?(\d+)k?/);
    if (priceMatch) {
      const price = parseInt(priceMatch[1] || priceMatch[2] || priceMatch[3]);
      criteria.maxPrice = price < 10000 ? price * 1000 : price;
    }
    
    const priceRangeMatch = lowerText.match(/\$?(\d+)k?\s*(?:to|-)\s*\$?(\d+)k?/);
    if (priceRangeMatch) {
      const min = parseInt(priceRangeMatch[1]);
      const max = parseInt(priceRangeMatch[2]);
      criteria.minPrice = min < 10000 ? min * 1000 : min;
      criteria.maxPrice = max < 10000 ? max * 1000 : max;
    }
    
    // Extract bedrooms
    const bedMatch = lowerText.match(/(\d+)\s*(?:bed|bedroom|br)/);
    if (bedMatch) {
      criteria.bedrooms = parseInt(bedMatch[1]);
    }
    
    // Extract bathrooms
    const bathMatch = lowerText.match(/(\d+)\s*(?:bath|bathroom)/);
    if (bathMatch) {
      criteria.bathrooms = parseInt(bathMatch[1]);
    }
    
    // Extract features
    criteria.features = [];
    if (lowerText.includes('pool')) criteria.features.push('pool');
    if (lowerText.includes('golf')) criteria.features.push('golf course');
    // Removed 'good schools' for Fair Housing compliance
    if (lowerText.includes('modern') || lowerText.includes('updated')) criteria.features.push('modern');
    if (lowerText.includes('luxury')) criteria.features.push('luxury');
    if (lowerText.includes('view')) criteria.features.push('view');
    if (lowerText.includes('single story')) criteria.features.push('single story');
    
    return criteria;
  };

  const handleAiSearch = async (query?: string, isRefinement: boolean = false) => {
    const searchText = query || searchQuery;
    if (!searchText.trim()) return;

    setSearchLoading(true);
    setLastSearchQuery(searchText);
    
    try {
      // Use Theta AI search endpoint which returns full property data
      // Let the backend parse the query - just like AIHero does
      const response = await axiosInstance.post('/theta-ai/search', {
        query: searchText,
        sessionId: `lead-${leadId}-${Date.now()}`,
        userId: leadId,
        includeAllResults: true
      });

      if (response.data) {
        // Handle response from theta-ai/search endpoint
        // The API returns { properties: [...], success: true/undefined }
        const properties = response.data.properties || [];
        
        console.log('🔍 Theta AI Search Response:', {
          hasData: !!response.data,
          hasProperties: !!response.data.properties,
          propertyCount: properties.length,
          firstProperty: properties[0]
        });
        
        // Skip detail fetch if properties already have enough data
        const needsDetailFetch = properties.length > 0 && 
          (!properties[0].address || !properties[0].images || !properties[0].description);
        
        let detailedProperties;
        if (needsDetailFetch) {
          // Get full property details for each property
          const propertyPromises = properties.map(async (prop: any) => {
            try {
              // Get full property details with all MLS data
              const detailResponse = await axiosInstance.get(`/properties/${prop.id}`);
              return detailResponse.data || prop;
            } catch (error) {
              console.log('Could not get detailed property data for', prop.id);
              return prop; // Return basic data if detail fetch fails
            }
          });
          detailedProperties = await Promise.all(propertyPromises);
        } else {
          // Properties already have sufficient data
          detailedProperties = properties;
        }
        
        // Map properties to ensure all fields are properly formatted
        const allProperties = detailedProperties.map((p: any) => ({
          ...p,
          // Core fields
          id: p.id || p.mls_number || p.mlsNumber,
          mls_number: p.mls_number || p.mlsNumber || p.mlsNum || p.id,
          
          // Address fields from the response data
          // If address is an object, extract the street, otherwise use as-is
          address: typeof p.address === 'object' && p.address !== null 
                   ? p.address.street 
                   : (p.address || p.street_address || p.streetAddress || 
                     `${p.streetNumber || ''} ${p.streetName || ''}`.trim() || 
                     'Address not available'),
          city: p.address?.city || p.city || 'Gilbert',
          state: p.address?.state || p.state || 'AZ',
          zip: p.address?.zip || p.zip || p.zipCode || p.postal_code || '',
          
          // Price and basic info
          price: p.price || p.list_price || p.listPrice || 0,
          bedrooms: p.bedrooms || p.beds || p.bedroomsTotal || 0,
          bathrooms: p.bathrooms || p.baths || p.bathroomsTotalDecimal || 0,
          square_feet: p.square_feet || p.sqft || p.squareFeet || p.buildingAreaTotal || 0,
          
          // Location data
          latitude: p.latitude || p.lat || p.coordinates?.latitude,
          longitude: p.longitude || p.lng || p.lon || p.coordinates?.longitude,
          
          // Images
          images: p.images || p.photos || p.media?.photos || [],
          
          // Listing info
          listingBrokerage: p.listingBrokerage || p.listing_brokerage || p.listingOfficeName || 'MLS Partner',
          listingAgent: p.listingAgent || p.listing_agent || p.listingAgentName || '',
          
          // Description
          description: p.description || p.remarks || p.publicRemarks || '',
          
          // Additional details
          year_built: p.year_built || p.yearBuilt || null,
          pool: p.pool || false,
          garage: p.garage || 0,
          
          // Match score
          matchScore: p.matchScore || p.match_score || p.score || 75
        }));
        
        // Identify top matches based on score
        const topMatches = allProperties.filter((p: any) => p.matchScore >= 80);
        
        setSearchResults({
          topMatches: topMatches.length > 0 ? topMatches : allProperties.slice(0, 10),
          allProperties: allProperties,
          totalCount: response.data.totalFound || allProperties.length,
          searchQuery: searchText,
          filters: response.data.criteria
        });
        
        // Show success if we have any properties
        if (allProperties.length > 0) {
          toast.success(`Found ${response.data.totalFound || allProperties.length} properties`);
          setShowRefinements(true);
          
          // Only save to history if this is a new search, not a refinement
          if (!isRefinement) {
            saveSearchToHistory(searchText, response.data.criteria || {}, allProperties);
          }
        } else {
          toast.error('No properties found matching your criteria.');
        }
      } else {
        toast.error('Failed to get response from search API.');
      }
    } catch (error: any) {
      console.error('AI Search error:', error);
      toast.error('Failed to search properties. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSaveSearch = async () => {
    if (!lastSearchQuery) return;

    try {
      const response = await axiosInstance.post(`/lead-engagement/leads/${leadId}/saved-searches`, {
        query: lastSearchQuery,
        criteria: searchResults,
        refinements: refinementText
      });

      if (response.data.success) {
        const newSearch = {
          id: response.data.data.id,
          query: lastSearchQuery,
          createdAt: new Date().toISOString(),
          resultsCount: searchResults?.totalCount
        };
        setSavedSearches([newSearch, ...savedSearches]);
        toast.success('Search saved successfully');
      }
    } catch (error: any) {
      // If endpoint doesn't exist yet, save locally
      if (error.response?.status === 404) {
        const newSearch = {
          id: Date.now().toString(),
          query: lastSearchQuery,
          createdAt: new Date().toISOString(),
          resultsCount: searchResults?.totalCount
        };
        setSavedSearches([newSearch, ...savedSearches]);
        toast.success('Search saved locally (backend integration pending)');
      } else {
        toast.error('Failed to save search');
      }
    }
  };

  const handleSaveProperty = async (property: any) => {
    try {
      const response = await axiosInstance.post(`/lead-engagement/leads/${leadId}/saved-properties`, {
        propertyId: property.id || property.mls_number,
        propertyData: property,
        savedFrom: 'ai_search',
        savedBy: 'agent',
        agentNotes: propertyNotes[property.id || property.mls_number] || ''
      });

      if (response.data.success) {
        toast.success('Property saved successfully');
      }
    } catch (error: any) {
      console.error('Save property error:', error);
      toast.error('Failed to save property');
    }
  };
  
  const togglePropertyExpanded = (propertyId: string) => {
    const newExpanded = new Set(expandedProperties);
    if (newExpanded.has(propertyId)) {
      newExpanded.delete(propertyId);
    } else {
      newExpanded.add(propertyId);
    }
    setExpandedProperties(newExpanded);
  };
  
  const handleSaveAgentNotes = (propertyId: string, notes: string) => {
    setPropertyNotes({ ...propertyNotes, [propertyId]: notes });
    toast.success('Agent notes saved');
  };
  
  const handleSendToClient = async (properties?: any[]) => {
    const propertiesToSend = properties || Array.from(selectedPropertiesToSend).map(id => 
      searchResults?.allProperties.find(p => (p.id || p.mls_number) === id)
    ).filter(Boolean);
    
    if (propertiesToSend.length === 0) {
      toast.error('Please select properties to send');
      return;
    }
    
    try {
      const response = await axiosInstance.post(`/lead-engagement/leads/${leadId}/send-properties`, {
        properties: propertiesToSend,
        message: `Hi ${clientName}, here are some properties I think you'll love based on your search criteria.`,
        agentNotes: propertyNotes
      });
      
      if (response.data.success) {
        toast.success(`Sent ${propertiesToSend.length} properties to ${clientName}`);
        setSelectedPropertiesToSend(new Set());
      }
    } catch (error) {
      console.error('Failed to send properties:', error);
      toast.error('Failed to send properties. Please try again.');
    }
  };

  const handleDeleteSavedSearch = (searchId: string) => {
    setSavedSearches(savedSearches.filter(s => s.id !== searchId));
    toast.success('Search removed');
  };

  const updateEmailFrequency = async (searchId: string, frequency: string) => {
    try {
      const response = await axiosInstance.put(`/lead-engagement/search-history/${searchId}/email-frequency`, {
        emailFrequency: frequency,
        isActive: frequency !== 'never'
      });

      if (response.data.success) {
        // Update local state
        setSearchHistory(searchHistory.map(s => 
          s.id === searchId ? { ...s, emailFrequency: frequency } : s
        ));
        toast.success(`Email alerts set to: ${frequency}`);
      }
    } catch (error) {
      console.error('Failed to update email frequency:', error);
      toast.error('Failed to update email settings');
    }
  };

  const handleApplyRefinements = () => {
    if (lastSearchQuery) {
      let refinedQuery = lastSearchQuery;
      
      // Check if refinement contains price-related terms
      // Pattern to match various price formats and ranges
      const pricePattern = /\b(?:under|over|below|above|max|min|price|between|around|approximately)\s*\$?\d+(?:,\d{3})*(?:\.\d+)?(?:k|K|m|M|million)?(?:\s*(?:to|and|-)\s*\$?\d+(?:,\d{3})*(?:\.\d+)?(?:k|K|m|M|million)?)?\b|\$\d+(?:,\d{3})*(?:\.\d+)?(?:k|K|m|M|million)?|\b\d+(?:,\d{3})+\b|\b\d+[kK]\b/gi;
      
      // If refinement has a price, remove any existing price from the original query
      if (refinementText.trim() && pricePattern.test(refinementText)) {
        // Remove all price-related terms from the original query
        refinedQuery = refinedQuery.replace(pricePattern, '').replace(/\s+/g, ' ').trim();
      }
      
      // Add refinement text if provided
      if (refinementText.trim()) {
        refinedQuery += ' ' + refinementText.trim();
      }
      
      // Add active filters
      if (activeFilters.length > 0) {
        refinedQuery += ' ' + activeFilters.join(' ');
      }
      
      // Run the enhanced search as a refinement (don't save to history)
      handleAiSearch(refinedQuery, true);
      
      // Clear refinements after applying
      setRefinementText('');
      setActiveFilters([]);
    }
  };

  const handleAddFilter = (filter: string) => {
    if (!activeFilters.includes(filter)) {
      setActiveFilters([...activeFilters, filter]);
    }
  };

  const handleRemoveFilter = (filter: string) => {
    setActiveFilters(activeFilters.filter(f => f !== filter));
  };

  // Quick filter suggestions
  const quickFilters = [
    'Pool required',
    'No HOA',
    'Single story only',
    'New construction',
    'Move-in ready',
    'Fixer-upper',
    'Corner lot',
    'Gated community',
    'Golf course view',
    'Mountain views'
  ];

  // Example searches based on buyer profile
  const getExampleSearches = () => {
    const examples = [];
    
    if (buyerProfile?.property_preferences) {
      const prefs = buyerProfile.property_preferences;
      const location = buyerProfile.location_preferences?.preferred_cities?.[0] || 'Phoenix';
      
      if (prefs.property_type === 'single_family') {
        examples.push(`Single family homes in ${location} with ${prefs.bedrooms_min || 3} bedrooms`);
      }
      if (prefs.budget_max) {
        examples.push(`Homes under $${(prefs.budget_max / 1000).toFixed(0)}k in ${location}`);
      }
    }

    // Only add defaults if no profile-based examples
    if (examples.length === 0) {
      examples.push(
        "3 bedroom home in Tempe with a pool under 500k",
        "Modern 4 bedroom home in Scottsdale under 800k",
        "Single story home in Chandler with a large yard"
      );
    }

    return examples.slice(0, 4);
  };

  return (
    <div className="ai-search-tab">
      {/* Search Header */}
      <div className="search-header mb-4">
        <h5 className="mb-3">
          <FontAwesomeIcon icon={faWandMagicSparkles} className="text-primary me-2" />
          AI-Powered Natural Language Property Search
        </h5>
        
        {/* Main Search Input */}
        <div className="search-input-wrapper">
          <Form.Group>
            <div className="input-group input-group-lg shadow-sm">
              <span className="input-group-text bg-white">
                <FontAwesomeIcon icon={faSearch} className="text-muted" />
              </span>
              <Form.Control
                type="text"
                placeholder='Try "3 bedroom homes in Gilbert under 500k with a pool"'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAiSearch()}
                className="border-start-0"
              />
              <Button 
                variant="primary"
                onClick={() => handleAiSearch()}
                disabled={searchLoading || !searchQuery.trim()}
              >
                {searchLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Searching...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagic} className="me-2" />
                    AI Search
                  </>
                )}
              </Button>
              {lastSearchQuery && (
                <Button 
                  variant="outline-success"
                  onClick={handleSaveSearch}
                  title="Save this search for future use"
                >
                  <FontAwesomeIcon icon={faBookmark} />
                </Button>
              )}
            </div>
            <Form.Text className="text-muted">
              Describe what {clientName} is looking for in natural language - our AI will understand
            </Form.Text>
          </Form.Group>
        </div>
      </div>

      {/* Saved Searches */}
      {/* HIDDEN: Saved Searches — duplicate of Recent Searches for now.
          Re-enable when AI Pro tier adds "watch this search" with new listing auto-matching.
          See KOPS-462 Phase 2 / KOPS-463 Phase 5 for feature gating. */}
      {false && savedSearches.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <h6 className="text-muted mb-3">
              <FontAwesomeIcon icon={faBookmark} className="me-2" />
              Saved Searches
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {savedSearches.map((search) => (
                <div 
                  key={search.id} 
                  className="saved-search-badge d-flex align-items-center"
                >
                  <Badge 
                    bg="light" 
                    text="dark" 
                    className="p-2 cursor-pointer border"
                    onClick={() => {
                      setSearchQuery(search.query);
                      handleAiSearch(search.query);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {search.query}
                    {search.resultsCount && (
                      <Badge bg="secondary" className="ms-2">{search.resultsCount}</Badge>
                    )}
                  </Badge>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 ms-1 text-muted"
                    onClick={() => handleDeleteSavedSearch(search.id!)}
                  >
                    <FontAwesomeIcon icon={faTimes} size="sm" />
                  </Button>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Search History */}
      {searchHistory.length > 0 && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faHistory} className="me-2" />
                Recent Searches for {clientName}
              </h6>
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowSearchHistory(!showSearchHistory)}
              >
                {showSearchHistory ? 'Hide' : 'Show'} History
              </Button>
            </div>
            
            {showSearchHistory && (
              <div className="search-history-list">
                {searchHistory.slice(0, 5).map((search, idx) => (
                  <div key={search.id || idx} className="search-history-item p-3 border rounded mb-2">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1">
                        <p className="mb-1 fw-semibold">"{search.query}"</p>
                        <small className="text-muted">
                          {search.resultsCount} properties found • 
                          {search.createdAt && new Date(search.createdAt).toLocaleDateString()}
                          {search.newListingsCount && search.newListingsCount > 0 && (
                            <Badge bg="success" className="ms-2">
                              +{search.newListingsCount} new
                            </Badge>
                          )}
                        </small>
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => {
                            setSearchQuery(search.query);
                            handleAiSearch(search.query);
                          }}
                        >
                          <FontAwesomeIcon icon={faSearch} className="me-1" />
                          Re-run
                        </Button>
                        {search.shareUrl && (
                          <Button
                            variant="outline-info"
                            size="sm"
                            onClick={() => {
                              navigator.clipboard.writeText(search.shareUrl || '');
                              toast.success('Share link copied to clipboard!');
                            }}
                            title="Copy shareable link for buyer"
                          >
                            <FontAwesomeIcon icon={faShare} className="me-1" />
                            Share
                          </Button>
                        )}
                        <Dropdown>
                          <Dropdown.Toggle variant="outline-secondary" size="sm">
                            <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                            {search.emailFrequency || 'Never'}
                          </Dropdown.Toggle>
                          <Dropdown.Menu>
                            <Dropdown.Item onClick={() => updateEmailFrequency(search.id!, 'never')}>Never</Dropdown.Item>
                            <Dropdown.Item onClick={() => updateEmailFrequency(search.id!, 'instant')}>Instant</Dropdown.Item>
                            <Dropdown.Item onClick={() => updateEmailFrequency(search.id!, 'daily')}>Daily</Dropdown.Item>
                            <Dropdown.Item onClick={() => updateEmailFrequency(search.id!, 'weekly')}>Weekly</Dropdown.Item>
                            <Dropdown.Item onClick={() => updateEmailFrequency(search.id!, 'monthly')}>Monthly</Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Search Refinements */}
      {showRefinements && searchResults && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">
                <FontAwesomeIcon icon={faFilter} className="me-2" />
                Refine Your Search
              </h6>
              <Button
                variant="link"
                size="sm"
                onClick={() => setShowRefinements(!showRefinements)}
              >
                {showRefinements ? 'Hide' : 'Show'} Refinements
              </Button>
            </div>
            
            {showRefinements && (
              <>
                {/* Natural Language Refinement */}
                <Form.Group className="mb-3">
                  <Form.Control
                    type="text"
                    placeholder='Add requirements like "must have pool" or "no HOA" or "near Whole Foods"'
                    value={refinementText}
                    onChange={(e) => setRefinementText(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleApplyRefinements()}
                  />
                  <Form.Text>
                    Add additional requirements in natural language
                  </Form.Text>
                </Form.Group>

                {/* Quick Filters */}
                <div className="mb-3">
                  <small className="text-muted d-block mb-2">Quick Filters:</small>
                  <div className="d-flex flex-wrap gap-2">
                    {quickFilters.map((filter) => (
                      <Badge
                        key={filter}
                        bg={activeFilters.includes(filter) ? 'primary' : 'light'}
                        text={activeFilters.includes(filter) ? 'white' : 'dark'}
                        className="p-2 cursor-pointer"
                        onClick={() => 
                          activeFilters.includes(filter) 
                            ? handleRemoveFilter(filter) 
                            : handleAddFilter(filter)
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        {filter}
                        {activeFilters.includes(filter) && (
                          <FontAwesomeIcon icon={faTimes} className="ms-1" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Active Filters Display */}
                {(activeFilters.length > 0 || refinementText) && (
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div className="flex-grow-1">
                        <small className="text-muted d-block">
                          {activeFilters.length} filters active
                          {refinementText && ' + custom refinements'}
                        </small>
                        <small className="text-primary">
                          <strong>New search:</strong> "{(() => {
                            let preview = lastSearchQuery;
                            const pricePattern = /\b(?:under|over|below|above|max|min|price|between|around|approximately)\s*\$?\d+(?:,\d{3})*(?:\.\d+)?(?:k|K|m|M|million)?(?:\s*(?:to|and|-)\s*\$?\d+(?:,\d{3})*(?:\.\d+)?(?:k|K|m|M|million)?)?\b|\$\d+(?:,\d{3})*(?:\.\d+)?(?:k|K|m|M|million)?|\b\d+(?:,\d{3})+\b|\b\d+[kK]\b/gi;
                            
                            // If refinement has a price, show it will replace the original
                            if (refinementText.trim() && pricePattern.test(refinementText)) {
                              preview = preview.replace(pricePattern, '').replace(/\s+/g, ' ').trim();
                            }
                            
                            if (refinementText) preview += ' ' + refinementText;
                            if (activeFilters.length > 0) preview += ' ' + activeFilters.join(' ');
                            return preview;
                          })()}"
                        </small>
                      </div>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleApplyRefinements}
                        className="ms-2"
                      >
                        Apply Refinements
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Search Results */}
      {searchResults && (
        <Tab.Container activeKey={resultsView} onSelect={(k) => setResultsView(k || 'top-matches')}>
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex justify-content-between align-items-center">
                <Nav variant="pills">
                  <Nav.Item>
                    <Nav.Link eventKey="top-matches">
                      <FontAwesomeIcon icon={faTrophy} className="me-2" />
                      Top AI Matches
                      {searchResults.topMatches && (
                        <Badge bg="primary" className="ms-2">{searchResults.topMatches.length}</Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey="all-results">
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      All Results
                      {searchResults.allProperties && (
                        <Badge bg="secondary" className="ms-2">{searchResults.allProperties.length}</Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                
                {/* View Mode Toggle for All Results */}
                {resultsView === 'all-results' && searchResults.allProperties.length > 0 && (
                  <div className="d-flex gap-2 align-items-center">
                    {selectedPropertiesToSend.size > 0 && (
                      <Button
                        variant="success"
                        size="sm"
                        onClick={() => handleSendToClient()}
                      >
                        <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                        Send {selectedPropertiesToSend.size} to Client
                      </Button>
                    )}
                    <div className="btn-group btn-group-sm">
                      <Button
                        variant={viewMode === 'cards' ? 'primary' : 'outline-secondary'}
                        onClick={() => setViewMode('cards')}
                      >
                        <FontAwesomeIcon icon={faList} className="me-1" />
                        Cards
                      </Button>
                      <Button
                        variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                        onClick={() => setViewMode('table')}
                      >
                        <FontAwesomeIcon icon={faTable} className="me-1" />
                        Table
                      </Button>
                      <Button
                        variant={viewMode === 'map' ? 'primary' : 'outline-secondary'}
                        onClick={() => setViewMode('map')}
                      >
                        <FontAwesomeIcon icon={faMap} className="me-1" />
                        Map
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </Card.Header>
            
            <Card.Body>
              <Tab.Content>
                {/* Top AI Matches */}
                <Tab.Pane eventKey="top-matches">
                  {searchResults.topMatches.length > 0 ? (
                    <Row>
                      {searchResults.topMatches.map((property, index) => {
                        const propertyId = property.id || property.mls_number || `property-${index}`;
                        const isExpanded = expandedProperties.has(propertyId);
                        
                        return (
                          <Col lg={12} className="mb-4" key={propertyId}>
                            <div className="position-relative">
                              {property.matchScore >= 90 && !isExpanded && (
                                <div className="position-absolute top-0 end-0 p-2 z-1">
                                  <Badge bg="success">
                                    <FontAwesomeIcon icon={faTrophy} className="me-1" />
                                    Perfect Match
                                  </Badge>
                                </div>
                              )}
                              
                              <CompactPropertyCard 
                                property={property}
                                onView={() => togglePropertyExpanded(propertyId)}
                                onSave={() => handleSaveProperty(property)}
                                onSendToClient={() => handleSendToClient([property])}
                                matchScore={property.matchScore}
                                showDescription={true}
                              />
                              
                              {/* Inline Property Details */}
                              {isExpanded && (
                                <PropertyDetailsInline
                                  property={property}
                                  isExpanded={isExpanded}
                                  onToggleExpand={() => togglePropertyExpanded(propertyId)}
                                  agentNotes={propertyNotes[propertyId]}
                                  onSaveNotes={(notes) => handleSaveAgentNotes(propertyId, notes)}
                                  onSaveProperty={() => handleSaveProperty(property)}
                                  onScheduleTour={() => toast.success('Schedule tour feature coming soon!')}
                                  leadName={clientName}
                                  matchScore={property.matchScore}
                                  aiReasoning={property.aiReasoning}
                                />
                              )}
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  ) : (
                    <Alert variant="info" className="text-center">
                      <FontAwesomeIcon icon={faMagic} className="me-2" />
                      No perfect matches found. Try adjusting your search criteria or check the "All Results" tab.
                    </Alert>
                  )}
                </Tab.Pane>

                {/* All Results */}
                <Tab.Pane eventKey="all-results">
                  {searchResults.allProperties.length > 0 ? (
                  viewMode === 'table' ? (
                    // Table View
                    <div className="table-responsive">
                        <table className="table table-hover align-middle">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '50px' }}></th>
                              <th>Property</th>
                              <th>Price</th>
                              <th>Beds/Baths</th>
                              <th>Sq Ft</th>
                              <th>Year</th>
                              <th>Match</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {searchResults.allProperties.map((property, index) => {
                              const propertyId = property.id || property.mls_number || `property-${index}`;
                              return (
                              <tr key={propertyId}>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={selectedPropertiesToSend.has(propertyId)}
                                    onChange={(e) => {
                                      const newSelected = new Set(selectedPropertiesToSend);
                                      if (e.target.checked) {
                                        newSelected.add(propertyId);
                                      } else {
                                        newSelected.delete(propertyId);
                                      }
                                      setSelectedPropertiesToSend(newSelected);
                                    }}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <img
                                      src={property.images?.[0] || property.image || '/placeholder-home.jpg'}
                                      alt={property.address || 'Property image'}
                                      style={{
                                        width: '60px',
                                        height: '45px',
                                        objectFit: 'cover',
                                        borderRadius: '4px',
                                        marginRight: '12px'
                                      }}
                                    />
                                    <div>
                                      <strong>{property.address || 'Address not available'}</strong>
                                      <br />
                                      <small className="text-muted">
                                        {property.city}, {property.state} {property.zip}
                                      </small>
                                      {property.mls_number && (
                                        <div>
                                          <Badge bg="light" text="dark" className="mt-1">
                                            MLS# {property.mls_number}
                                          </Badge>
                                        </div>
                                      )}
                                      <div>
                                        <span className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 500 }}>
                                          <FontAwesomeIcon icon={faBuilding} className="me-1" />
                                          Listing courtesy of {property.listingAgent?.officeName || property.listingBrokerage || 'MLS Partner'}
                                          {property.listingAgent?.name && (
                                            <span> - {property.listingAgent.name}</span>
                                          )}
                                        </span>
                                        <p className="text-muted mb-0" style={{ fontSize: '0.65rem', fontStyle: 'italic' }}>
                                          This property is not representative of the displaying agent's or brokerage's transaction history.
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="fw-bold text-primary">
                                  ${property.price?.toLocaleString()}
                                </td>
                                <td>
                                  {property.bedrooms || property.beds || 0} / {property.bathrooms || property.baths || 0}
                                </td>
                                <td>
                                  {property.square_feet?.toLocaleString() || 'N/A'}
                                </td>
                                <td>
                                  {property.year_built || 'N/A'}
                                </td>
                                <td>
                                  <Badge 
                                    bg={
                                      property.matchScore >= 80 ? 'success' : 
                                      property.matchScore >= 60 ? 'warning' : 
                                      'secondary'
                                    }
                                  >
                                    {property.matchScore || 0}%
                                  </Badge>
                                </td>
                                <td>
                                  <div className="btn-group btn-group-sm">
                                    <Button 
                                      variant="outline-primary" 
                                      onClick={() => console.log('View:', property.id)}
                                    >
                                      View
                                    </Button>
                                    <Button 
                                      variant="outline-success" 
                                      onClick={() => handleSaveProperty(property)}
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : viewMode === 'map' ? (
                      // Card View
                      <Row>
                        {searchResults.allProperties.map((property, index) => {
                          const propertyId = property.id || property.mls_number || `property-${index}`;
                          const isExpanded = expandedProperties.has(propertyId);
                          
                          return (
                            <Col lg={12} className="mb-4" key={propertyId}>
                              <CompactPropertyCard 
                                property={property}
                                onView={() => togglePropertyExpanded(propertyId)}
                                onSave={() => handleSaveProperty(property)}
                                onSendToClient={() => handleSendToClient([property])}
                                matchScore={property.matchScore}
                                showDescription={true}
                              />
                              
                              {/* Inline Property Details */}
                              {isExpanded && (
                                <PropertyDetailsInline
                                  property={property}
                                  isExpanded={isExpanded}
                                  onToggleExpand={() => togglePropertyExpanded(propertyId)}
                                  agentNotes={propertyNotes[propertyId]}
                                  onSaveNotes={(notes) => handleSaveAgentNotes(propertyId, notes)}
                                  onSaveProperty={() => handleSaveProperty(property)}
                                  onScheduleTour={() => toast.success('Schedule tour feature coming soon!')}
                                  leadName={clientName}
                                  matchScore={property.matchScore}
                                  aiReasoning={property.aiReasoning}
                                />
                              )}
                            </Col>
                          );
                        })}
                      </Row>
                    ) : (
                      // Map View
                      <div className="map-view-container">
                        <div className="mb-3 d-flex justify-content-between align-items-center">
                          <p className="text-muted mb-0">
                            Showing {searchResults.allProperties.filter(p => p.latitude && p.longitude).length} of {searchResults.allProperties.length} properties on map
                          </p>
                          {selectedPropertiesToSend.size > 0 && (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleSendToClient()}
                            >
                              <FontAwesomeIcon icon={faEnvelope} className="me-1" />
                              Send {selectedPropertiesToSend.size} to Client
                            </Button>
                          )}
                        </div>
                        <PropertyMapbox
                          properties={searchResults.allProperties
                            .filter(prop => prop.latitude && prop.longitude) // Only show properties with coordinates
                            .map((prop, idx) => ({
                            id: prop.id || prop.mls_number || `prop-${idx}`,
                            address: prop.address || prop.street_address,
                            latitude: prop.latitude,
                            longitude: prop.longitude,
                            price: prop.price || prop.list_price,
                            bedrooms: prop.bedrooms || prop.beds || 0,
                            bathrooms: prop.bathrooms || prop.baths || 0,
                            square_feet: prop.square_feet || prop.sqft || 0,
                            status: 'Active',
                            matchScore: prop.matchScore,
                            listingBrokerage: prop.listingBrokerage,
                            listingAgent: prop.listingAgent,
                            mls_number: prop.mls_number
                          }))}
                          selectedPropertyId={Array.from(expandedProperties)[0]}
                          onPropertySelect={(property) => {
                            const propertyId = property.id;
                            togglePropertyExpanded(propertyId);
                          }}
                          height="500px"
                          showNavigation={true}
                        />
                        
                        {/* Property List Sidebar for Map View */}
                        <div className="map-property-list mt-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                          {searchResults.allProperties.map((property, index) => {
                            const propertyId = property.id || property.mls_number || `property-${index}`;
                            return (
                              <CompactPropertyCard
                                key={propertyId}
                                property={{
                                  ...property,
                                  bedrooms: property.bedrooms || property.beds,
                                  bathrooms: property.bathrooms || property.baths,
                                  state: property.state || 'AZ'
                                }}
                                onView={() => togglePropertyExpanded(propertyId)}
                                onSave={() => handleSaveProperty(property)}
                                onSendToClient={() => handleSendToClient([property])}
                                matchScore={property.matchScore}
                                showDescription={false}
                                className="mb-2"
                              />
                            );
                          })}
                        </div>
                      </div>
                    )
                  ) : (
                    <Alert variant="info" className="text-center">
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      No properties found matching your criteria. Try broadening your search.
                    </Alert>
                  )}
                </Tab.Pane>
              </Tab.Content>
            </Card.Body>
          </Card>
        </Tab.Container>
      )}

      {/* Initial State - No Search Yet */}
      {!searchResults && !searchLoading && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon icon={faWandMagicSparkles} size="3x" className="text-primary mb-3" />
            <h5 className="mb-3">Start with a Natural Language Search</h5>
            <p className="text-muted mb-4">
              Describe what {clientName} is looking for and let AI find the perfect matches
            </p>
            
            <div className="search-examples mt-4">
              <h6 className="text-muted mb-3">Try these example searches:</h6>
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {getExampleSearches().map((example, idx) => (
                  <Badge 
                    key={idx} 
                    bg="light" 
                    text="dark" 
                    className="p-3 cursor-pointer search-example-badge"
                    onClick={() => {
                      setSearchQuery(example);
                      handleAiSearch(example);
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    {example}
                  </Badge>
                ))}
              </div>
            </div>
            
            {buyerProfile && (
              <div className="mt-4">
                <small className="text-muted">
                  Based on {clientName}'s profile, we'll prioritize properties in{' '}
                  {buyerProfile.location_preferences?.preferred_cities?.join(', ') || 'their preferred areas'}
                </small>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Loading State */}
      {searchLoading && (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="mb-4">
              <Spinner animation="border" variant="primary" />
            </div>
            <h5 className="mb-2">AI is searching for the perfect properties...</h5>
            <p className="text-muted mb-0">
              Analyzing thousands of listings to find matches for "{lastSearchQuery}"
            </p>
            <small className="text-muted">This usually takes 2-5 seconds</small>
          </Card.Body>
        </Card>
      )}
      
      {/* MLS Attribution Footer */}
      {searchResults && searchResults.allProperties.length > 0 && (
        <div className="mls-attribution mt-4 p-3 bg-light rounded text-center" style={{
          fontSize: '11px',
          color: '#6c757d'
        }}>
          <p className="mb-1">
            Listing information provided courtesy of participating brokers/agents in the Arizona Regional Multiple Listing Service, Inc. (ARMLS). 
            The data relating to real estate for sale on this website comes in part from the Internet Data Exchange (IDX) Program of ARMLS.
          </p>
          <p className="mb-0">
            IDX information is provided exclusively for consumers' personal, non-commercial use and may not be used for any purpose other than to 
            identify prospective properties consumers may be interested in purchasing. All information is deemed reliable but is not guaranteed 
            accurate by the MLS. © {new Date().getFullYear()} Arizona Regional Multiple Listing Service, Inc. All rights reserved.
          </p>
        </div>
      )}
    </div>
  );
};

export default AISearchTab;